import { useEffect, useState } from "react";

import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSaveInfraSettings } from "@/features/servers/hooks/useInfraSettings";
import type { ISoftwareType } from "@/features/servers/schemas/servers";
import { SEMVER_PATTERN } from "@/features/servers/schemas/servers";

interface TargetVersionsEditorProps {
  softwareTypes: ISoftwareType[];
  targetVersions: Record<string, string>;
}

/**
 * Edita versiones objetivo (0.0.0) por tipo de software en viewmed_config.
 */
export function TargetVersionsEditor({
  softwareTypes,
  targetVersions,
}: TargetVersionsEditorProps) {
  const saveSettings = useSaveInfraSettings();
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const t of softwareTypes.filter((x) => x.active)) {
      next[t.key] = targetVersions[t.key] ?? targetVersions[t.id] ?? "";
    }
    setDraft(next);
  }, [softwareTypes, targetVersions]);

  const handleSave = async () => {
    for (const [key, value] of Object.entries(draft)) {
      const v = value.trim();
      if (v && !SEMVER_PATTERN.test(v)) {
        toast.error(`"${key}": usá formato 0.0.0`);
        return;
      }
    }
    try {
      await saveSettings.mutateAsync({ softwareTargetVersions: draft });
      toast.success("Versiones objetivo guardadas");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "No se pudieron guardar las versiones"
      );
    }
  };

  const activeTypes = softwareTypes.filter((t) => t.active);

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium">
          Versiones objetivo (global)
        </CardTitle>
        <CardDescription>
          Versión que ViewMed maneja de cada software (formato 0.0.0). Sirve para
          detectar instalaciones desactualizadas.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {activeTypes.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin tipos de software.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {activeTypes.map((t) => (
              <div key={t.id} className="space-y-1.5">
                <Label htmlFor={`target-${t.key}`} className="text-xs">
                  {t.displayName}
                </Label>
                <Input
                  id={`target-${t.key}`}
                  value={draft[t.key] ?? ""}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [t.key]: e.target.value }))
                  }
                  placeholder="0.0.0"
                  className="font-mono"
                />
              </div>
            ))}
          </div>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handleSave()}
          disabled={saveSettings.isPending || activeTypes.length === 0}
        >
          {saveSettings.isPending ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1.5" />
          )}
          Guardar versiones
        </Button>
      </CardContent>
    </Card>
  );
}
